import { Directive, Input, booleanAttribute, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  ChangeCallback,
  FMNTS_FORMS_FORCE_AUTOCOMPLETE_OFF,
  FMNTS_FORMS_OPTIONS,
  TouchedCallback,
} from '@fmnts/common/forms';
import {
  DeclarativeForm,
  FieldDefinition,
  FieldsetDefinition,
  FormDeclaration,
  IDeclarativeFieldInputComponent,
  IDeclarativeFieldOutputComponent,
} from '@fmnts/declarative-forms/core';

/**
 * Abstract base class for a declarative field.
 */
@Directive()
export abstract class AbstractDeclarativeFieldComponent<
  TField extends FieldDefinition,
> implements IDeclarativeFieldOutputComponent
{
  @Input({ required: true }) declaration!: FormDeclaration;
  @Input({ required: true }) field!: TField;
  @Input({ required: true }) fieldset!: FieldsetDefinition;
  @Input({ required: true }) form!: DeclarativeForm;
}

/**
 * Abstract base class for a declarative field that is
 * tied to a control.
 */
@Directive()
export abstract class AbstractDeclarativeInputFieldComponent<
    TField extends FieldDefinition,
    TValue,
  >
  extends AbstractDeclarativeFieldComponent<TField>
  implements IDeclarativeFieldInputComponent<TValue>
{
  public isDisabled = false;

  /** Control tied to the field. */
  @Input({ required: true }) control!: AbstractControl<TValue | null>;
  /**
   * If `true`, the control is readonly.
   * This means that it is not excluded from form validation.
   */
  @Input({ required: true, transform: booleanAttribute }) isReadonly = false;

  abstract writeValue(obj: unknown): void;

  protected _onChange: ChangeCallback<TValue | null> = () => {};
  protected _onTouched: TouchedCallback = () => {};

  registerOnChange(fn: ChangeCallback): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: TouchedCallback): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * If your field supports autocomplete, you can infer the default behavior
   * by calling this method.
   *
   * @returns
   * Initial value for the `autocomplete` attribute of the input field.
   */
  protected _getAutocompleteBehavior(
    formOpts = inject(FMNTS_FORMS_OPTIONS),
    forceAutocompleteOffValue = inject(FMNTS_FORMS_FORCE_AUTOCOMPLETE_OFF),
  ): AutoFill | null {
    if (formOpts.forceAutocompleteOff) {
      return forceAutocompleteOffValue;
    }

    return null;
  }
}
